/**
 * ============================================================
 * Yamatrip ツアーカレンダー JavaScript 改善版 v2
 * 
 * 改善ポイント:
 * - 価格クラスを「週末/平日」ではなく「金額」で判定
 * - 閾値はバリエーションの実データから算出（ハードコードしない）。
 *   料金が1種類しかない商品では強調そのものを行わない
 * 
 * 更新日: 2026-11-02
 * ============================================================
 */

(function() {
  'use strict';

  // ========================================
  // 設定
  // ========================================
  const CONFIG = {
    // 表示する年月と lowPriceThreshold はバリエーションのデータから毎回算出する。
    // 年や料金が変わるたびにここを書き換える必要がないようにするため。
    // （2026→2027の改定時に書き換え漏れでカレンダーが前年のまま表示される
    //   不具合があったため、ハードコードをやめた）
    // 月も同様にハードコードしない。夏の富士ツアー（7〜9月）以外に
    // 冬ツアー（1〜3月など）も同じカレンダーを使うため
    // 価格の閾値（この金額未満は「安い」扱い）
    // Shopifyの価格は「分」単位（¥25,980 = 2598000）
    lowPriceThreshold: null,
    // ステータス設定（既定は3種類 + 催行中止は―表示。
    // 山小屋のない商品は init で 満席・残りわずか を追加する）
    statusConfig: {
      '募集中': { shape: '○', bgClass: 'tour-bg-recruiting', cssClass: 'recruiting', priority: 1 },
      '催行決定': { shape: '●', bgClass: 'tour-bg-confirmed', cssClass: 'confirmed', priority: 2 },
      '催行中止': { shape: '―', bgClass: 'tour-bg-cancelled', cssClass: 'none', priority: 0 }
    },
    // 背景色の優先度（行動喚起優先：催行決定 > 募集中、催行中止は無視）
    bgPriority: {
      '催行中止': -1,
      '募集中': 1,
      '催行決定': 2
    }
  };

  // ========================================
  // ユーティリティ関数
  // ========================================
  
  /**
   * 指定月の日数を取得
   */
  function getDaysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
  }

  /**
   * 指定日の曜日を取得（0=日, 1=月, ... 6=土）
   */
  function getDayOfWeek(year, month, day) {
    return new Date(year, month - 1, day).getDay();
  }

  /**
   * 月曜始まりの曜日インデックスに変換（0=月, 1=火, ... 6=日）
   */
  function getMondayBasedDayIndex(year, month, day) {
    const dow = getDayOfWeek(year, month, day);
    return dow === 0 ? 6 : dow - 1;
  }

  /**
   * 価格をフォーマット
   */
  function formatPrice(price) {
    return '¥' + Math.floor(price / 100).toLocaleString();
  }

  /**
   * 価格クラスを判定（金額ベース）
   */
  function getPriceClass(price) {
    // 全日程が同一料金の商品（冬ツアーなど）は閾値が null。
    // 「安い日」の強調が全日に付いてカレンダー全体がオレンジになるのを避けるため、
    // クラスを付けずに通常の文字色で出す
    if (CONFIG.lowPriceThreshold === null) return '';
    return price < CONFIG.lowPriceThreshold ? 'tour-price-low' : 'tour-price-high';
  }

  /**
   * バリエーションから「年月」の一覧を重複排除して古い順に取り出す
   */
  function collectPeriods(variants) {
    const seen = {};
    const periods = [];
    variants.forEach(v => {
      if (typeof v.year !== 'number' || typeof v.month !== 'number') return;
      const key = `${v.year}-${v.month}`;
      if (seen[key]) return;
      seen[key] = true;
      periods.push({ year: v.year, month: v.month });
    });
    return periods.sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month));
  }

  /**
   * 年月を一意に表すキー（DOMのid・data属性に使う）
   */
  function getPeriodKey(period) {
    return `${period.year}-${period.month}`;
  }

  /**
   * 月名を取得（ロケールに未定義の月は「数字＋月接尾辞」にフォールバック）
   */
  function getMonthName(month, translations) {
    return translations.monthNames[month] || (month + translations.monthSuffix);
  }

  /**
   * 月ラベルを取得。複数年にまたがる場合だけ年を併記する
   * （冬ツアーのように12月→1月と年をまたぐ日程があるため）
   */
  function getPeriodLabel(period, translations, multiYear) {
    const monthName = getMonthName(period.month, translations);
    if (!multiYear) return monthName;
    return translations.yearFormat.replace('{year}', period.year).replace('{month}', monthName);
  }

  /**
   * 山小屋の短縮名を取得
   */
  function getHutShortName(hutName) {
    return CONFIG.hutShortNames[hutName] || hutName;
  }

  // ========================================
  // カレンダーデータ処理
  // ========================================

  /**
   * バリエーションデータを日付ごとにグループ化
   */
  function groupVariantsByDate(variants) {
    const grouped = {};
    
    variants.forEach(v => {
      const key = `${v.year}-${v.month}-${v.day}`;
      if (!grouped[key]) {
        grouped[key] = {
          year: v.year,
          month: v.month,
          day: v.day,
          huts: []
        };
      }
      grouped[key].huts.push({
        id: v.id,
        hut: v.hut,
        status: v.status,
        price: v.price,
        inventory: v.inventory,
        available: v.available
      });
    });
    
    return grouped;
  }

  /**
   * 日付データの背景色クラスを決定（最も優先度の高いステータスを採用）
   */
  function determineBgClass(huts) {
    // すべての山小屋が催行中止かどうかをチェック
    const allCancelled = huts.every(h => h.status === '催行中止');
    if (allCancelled) {
      return 'tour-disabled';
    }
    
    let highestPriority = -1;
    let bgClass = 'tour-bg-recruiting';
    
    huts.forEach(h => {
      // 催行中止は背景色の判定から除外
      if (h.status === '催行中止') return;
      
      const priority = CONFIG.bgPriority[h.status] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        bgClass = CONFIG.statusConfig[h.status]?.bgClass || 'tour-bg-recruiting';
      }
    });
    
    return bgClass;
  }

  /**
   * すべての山小屋が催行中止かどうかを判定
   */
  function isAllCancelled(huts) {
    return huts.every(h => h.status === '催行中止');
  }

  /**
   * ステータス表示HTMLを生成
   * hutOrder に含まれる全山小屋について、データがなければ「―」を表示
   */
  function generateStatusDisplay(huts, fewLeftThreshold, hutOrder) {
    // 山小屋メタフィールドを持たない商品（冬ツアーなど）は hutOrder が空になる。
    // その場合は山小屋で引き当てず、その日のバリエーションのステータスをそのまま出す
    if (hutOrder.length === 0) {
      return huts.map(hutData => {
        const config = CONFIG.statusConfig[hutData.status] || CONFIG.statusConfig['募集中'];
        return `<span class="tour-status-shape tour-shape-${config.cssClass}">${config.shape}</span>`;
      }).join('<span class="tour-status-separator">/</span>');
    }

    // hutOrder の各山小屋について表示を生成
    const parts = hutOrder.map(hutName => {
      // この山小屋のデータを探す
      const hutData = huts.find(h => h.hut === hutName);
      
      if (!hutData) {
        // この日にこの山小屋のバリエーションがない場合は「―」
        return '<span class="tour-status-shape tour-shape-none">―</span>';
      }
      
      const config = CONFIG.statusConfig[hutData.status] || CONFIG.statusConfig['募集中'];
      let display = `<span class="tour-status-shape tour-shape-${config.cssClass}">${config.shape}</span>`;
      
      // 残席5以下かつ募集中または催行決定の場合、残席数を表示
      /* 残席数表示は残席状況カレンダーに移行
      if ((hutData.status === '募集中' || hutData.status === '催行決定') && hutData.inventory <= fewLeftThreshold && hutData.inventory > 0) {
        display += `<span class="tour-inventory-count">${hutData.inventory}</span>`;
      }
      */
      
      return display;
    });

    return parts.join('<span class="tour-status-separator">/</span>');
  }

  // ========================================
  // カレンダーHTML生成
  // ========================================

  /**
   * デスクトップ版カレンダーHTMLを生成
   */
  function generateDesktopCalendar(period, groupedData, fewLeftThreshold, hutOrder, isFirst = false, translations) {
    const year = period.year;
    const month = period.month;
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayIndex = getMondayBasedDayIndex(year, month, 1);
    const monthName = getMonthName(month, translations);
    
    let html = `
      <div class="tour-calendar-month${isFirst ? ' active' : ''}" id="tour-calendar-${getPeriodKey(period)}">
        <div class="tour-trip-calendar-container">
          <table class="tour-month-calendar">
            <caption>${translations.yearFormat.replace('{year}', year).replace('{month}', monthName)}</caption>
            <thead>
              <tr>
                ${translations.weekdays.map((w, i) => `<th class="${i >= 5 ? 'tour-weekend' : ''}">${w}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
    `;

    let currentDay = 1;
    let weekHtml = '<tr>';
    
    // 最初の週の空セル
    for (let i = 0; i < firstDayIndex; i++) {
      weekHtml += '<td class="tour-empty-cell"><span class="tour-day-number"></span></td>';
    }

    // 日付セルを生成
    while (currentDay <= daysInMonth) {
      const dayIndex = getMondayBasedDayIndex(year, month, currentDay);
      const key = `${year}-${month}-${currentDay}`;
      const dateData = groupedData[key];
      
      if (dayIndex === 0 && currentDay > 1) {
        weekHtml += '</tr><tr>';
      }

      if (dateData) {
        const bgClass = determineBgClass(dateData.huts);
        const allCancelled = isAllCancelled(dateData.huts);
        
        if (allCancelled) {
          // すべて催行中止の場合はツアーがない日と同じ表示（「―」のみ）
          weekHtml += `
            <td class="tour-disabled" data-date="${key}">
              <span class="tour-day-number">${currentDay}</span>
              <span class="tour-price">―</span>
            </td>
          `;
        } else {
          const statusDisplay = generateStatusDisplay(dateData.huts, fewLeftThreshold, hutOrder);
          // 最初の山小屋の価格を表示
          const price = dateData.huts[0]?.price || 0;
          // 価格クラスを金額で判定
          const priceClass = getPriceClass(price);
          
          weekHtml += `
            <td class="${bgClass}" data-date="${key}">
              <span class="tour-day-number">${currentDay}</span>
              <span class="tour-price ${priceClass}">${formatPrice(price)}</span>
              <div class="tour-status-row">${statusDisplay}</div>
            </td>
          `;
        }
      } else {
        // ツアーがない日（「―」のみ表示）
        weekHtml += `
          <td class="tour-disabled">
            <span class="tour-day-number">${currentDay}</span>
            <span class="tour-price">―</span>
          </td>
        `;
      }

      currentDay++;
    }

    // 最後の週の空セル
    const lastDayIndex = getMondayBasedDayIndex(year, month, daysInMonth);
    for (let i = lastDayIndex + 1; i < 7; i++) {
      weekHtml += '<td class="tour-empty-cell"><span class="tour-day-number"></span></td>';
    }
    weekHtml += '</tr>';

    html += weekHtml;
    html += `
            </tbody>
          </table>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * モバイル版リストHTMLを生成
   */
  function generateMobileList(period, groupedData, fewLeftThreshold, hutOrder, isFirst = false, translations, multiYear = false) {
    const year = period.year;
    const month = period.month;
    const daysInMonth = getDaysInMonth(year, month);
    const monthName = getMonthName(month, translations);
    const monthLabel = translations.mobileMonthLabel.replace('{month}', getPeriodLabel(period, translations, multiYear));
    
    let listItems = '';
    
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${year}-${month}-${day}`;
      const dateData = groupedData[key];
      
      if (dateData) {
        const allCancelled = isAllCancelled(dateData.huts);
        
        // すべて催行中止の場合はリストに表示しない
        if (allCancelled) {
          continue;
        }
        
        const bgClass = determineBgClass(dateData.huts);
        const dayIndex = getMondayBasedDayIndex(year, month, day);
        const weekdayName = translations.weekdays[dayIndex];
        const statusDisplay = generateStatusDisplay(dateData.huts, fewLeftThreshold, hutOrder);
        const price = dateData.huts[0]?.price || 0;
        // 価格クラスを金額で判定
        const priceClass = getPriceClass(price);
        
        // 日付フォーマットを適用
        const dateFormat = translations.dateFormat || '{month}{day}日（{weekday}）';
        const dateLabel = dateFormat
          .replace('{month}', monthName)
          .replace('{day}', day)
          .replace('{weekday}', weekdayName);

        listItems += `
          <div class="tour-date-item ${bgClass}" data-date="${key}">
            <div class="tour-date-info">
              <span class="tour-date-label">${dateLabel}</span>
            </div>
            <div class="tour-price-info">
              <span class="tour-price-amount ${priceClass}">${formatPrice(price)}</span>
            </div>
            <div class="tour-status-info">${statusDisplay}</div>
          </div>
        `;
      }
    }
    
    const isFirstMonth = isFirst;
    
    return `
      <div class="tour-mobile-month-section">
        <button class="tour-month-toggle${isFirst ? ' active' : ''}" data-toggle="tour-mobile-${getPeriodKey(period)}">
          <span class="tour-month-toggle-text">
            <span class="tour-month-icon">📅</span>
            <span>${monthLabel}</span>
          </span>
          <span class="tour-toggle-arrow">${isFirst ? '▲' : '▼'}</span>
        </button>
        <div class="tour-month-date-list${isFirstMonth ? ' active' : ''}" id="tour-mobile-${getPeriodKey(period)}">
          ${listItems}
        </div>
      </div>
    `;
  }

  // ========================================
  // イベントハンドラ
  // ========================================

  /**
   * デスクトップ版：月タブ切り替え
   */
  function handleTabClick(e) {
    const tab = e.target.closest('.tour-calendar-tab');
    if (!tab) return;
    
    const periodKey = tab.dataset.period;
    
    // タブのアクティブ状態を更新
    document.querySelectorAll('.tour-calendar-tab').forEach(t => t.classList.remove('active'));
    tab.classList.add('active');
    
    // カレンダーの表示を切り替え
    document.querySelectorAll('.tour-calendar-month').forEach(c => c.classList.remove('active'));
    const targetCalendar = document.getElementById(`tour-calendar-${periodKey}`);
    if (targetCalendar) {
      targetCalendar.classList.add('active');
    }
  }

  /**
   * モバイル版：月トグル
   */
  function handleToggleClick(e) {
    const button = e.target.closest('.tour-month-toggle');
    if (!button) return;
    
    const targetId = button.dataset.toggle;
    const monthList = document.getElementById(targetId);
    const arrow = button.querySelector('.tour-toggle-arrow');
    
    if (!monthList) return;
    
    if (monthList.classList.contains('active')) {
      monthList.classList.remove('active');
      button.classList.remove('active');
      if (arrow) arrow.textContent = '▼';
    } else {
      monthList.classList.add('active');
      button.classList.add('active');
      if (arrow) arrow.textContent = '▲';
    }
  }

  // ========================================
  // 初期化
  // ========================================

  function init() {
    // データを取得
    const dataScript = document.getElementById('tour-variant-data');
    if (!dataScript) {
      // ツアー商品ではない場合は何もしない
      return;
    }

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Yamatrip: Failed to parse tour variant data:', e);
      return;
    }

    if (!data.variants || data.variants.length === 0) {
      console.log('Yamatrip: No tour variants found');
      return;
    }

    const fewLeftThreshold = data.fewLeftThreshold || 5;
    const hutOrder = data.huts || [];

    // 山小屋のない商品（冬ツアーなど）は残席ウィジェットを持たないため、
    // 満席・残りわずかをこのカレンダー上で表示する。
    // 富士ツアー（山小屋あり）はこの2つを残席状況カレンダー側で見せる設計のため
    // 従来どおり○（募集中）のままにしておく。
    if (hutOrder.length === 0) {
      CONFIG.statusConfig['満席'] = { shape: '×', bgClass: 'tour-bg-full', cssClass: 'full', priority: 0 };
      CONFIG.statusConfig['残りわずか'] = { shape: '△', bgClass: 'tour-bg-recruiting', cssClass: 'few', priority: 1 };
      CONFIG.bgPriority['満席'] = 0;
      CONFIG.bgPriority['残りわずか'] = 1;
    }

    // 翻訳データを取得
    const translationsScript = document.getElementById('tour-calendar-translations');
    let translations = {
      monthSuffix: '月',
      monthNames: { '7': '7月', '8': '8月', '9': '9月' },
      weekdays: ['月', '火', '水', '木', '金', '土', '日'],
      mobileMonthLabel: '{month}の出発日と料金',
      dateFormat: '{month}{day}日（{weekday}）',
      yearFormat: '{year}年 {month}',
      hutShortNames: {},
      displayOrder: '表示順:'
    };
    
    if (translationsScript) {
      try {
        translations = JSON.parse(translationsScript.textContent);
      } catch (e) {
        console.error('Yamatrip: Failed to parse translations:', e);
      }
    }
    
    // 表示する年月を決める。年と月の組で扱うのは、冬ツアーのように
    // 12月→1月と年をまたぐ日程があるため（年だけで絞ると12月分が丸ごと消える）
    const allPeriods = collectPeriods(data.variants);
    if (allPeriods.length === 0) {
      console.log('Yamatrip: No departure year/month found');
      return;
    }

    // 終わった月は表示しない。
    // シーズン切替中は旧年度のバリエーションが残っていることがあるため
    const now = new Date();
    const currentIndex = now.getFullYear() * 12 + (now.getMonth() + 1);
    let periods = allPeriods.filter(pd => pd.year * 12 + pd.month >= currentIndex);

    // すべて過去（シーズン終了後）の場合は、最後のシーズンをそのまま出す。
    // カレンダーが空になって「日程がない商品」に見えるのを防ぐため
    if (periods.length === 0) {
      const latestYear = Math.max.apply(null, allPeriods.map(pd => pd.year));
      periods = allPeriods.filter(pd => pd.year === latestYear);
    }

    // 年をまたぐ場合だけタブに年を併記する
    const multiYear = periods.some(pd => pd.year !== periods[0].year);

    // 表示対象の年月のバリエーションだけを使う
    const periodKeys = periods.map(getPeriodKey);
    const yearVariants = data.variants.filter(v => periodKeys.indexOf(`${v.year}-${v.month}`) !== -1);

    // 「安い日」の判定閾値を実データから決める。
    // 最安料金の日を安い扱いにしたいので、最安とそれより上の最初の料金の中間を閾値にする
    const prices = yearVariants.map(v => v.price).filter(p => typeof p === 'number').sort((a, b) => a - b);
    const minPrice = prices[0];
    const nextPrice = prices.find(p => p > minPrice);
    // 料金が1種類しかない場合は閾値を持たせない（強調の対象がないため）
    CONFIG.lowPriceThreshold = nextPrice ? (minPrice + nextPrice) / 2 : null;

    // データをグループ化
    const groupedData = groupVariantsByDate(yearVariants);

    // デスクトップカレンダーを生成
    const desktopContainer = document.getElementById('tour-calendar-container');
    if (desktopContainer) {
      let desktopHtml = '';
      periods.forEach((period, index) => {
        desktopHtml += generateDesktopCalendar(period, groupedData, fewLeftThreshold, hutOrder, index === 0, translations);
      });
      desktopContainer.innerHTML = desktopHtml;
    }

    // モバイルリストを生成
    const mobileContainer = document.getElementById('tour-mobile-calendar-container');
    if (mobileContainer) {
      let mobileHtml = '';
      periods.forEach((period, index) => {
        mobileHtml += generateMobileList(period, groupedData, fewLeftThreshold, hutOrder, index === 0, translations, multiYear);
      });
      mobileContainer.innerHTML = mobileHtml;
    }

    // 月タブを動的に生成
    const tabsContainer = document.querySelector('.tour-calendar-tabs');
    if (tabsContainer) {
      let tabsHtml = '';
      periods.forEach((period, index) => {
        const label = getPeriodLabel(period, translations, multiYear);
        tabsHtml += `<button class="tour-calendar-tab${index === 0 ? ' active' : ''}" data-period="${getPeriodKey(period)}">${label}</button>`;
      });
      tabsContainer.innerHTML = tabsHtml;
    }

    // 山小屋凡例を生成（2つ以上の場合）
    const hutLegendContainer = document.getElementById('tour-hut-legend');
    if (hutLegendContainer && hutOrder.length > 1) {
      const hutLabels = hutOrder.map(hut => {
        return translations.hutShortNames[hut] || hut;
      }).join(' / ');
      hutLegendContainer.innerHTML = `<span class="tour-hut-legend-label">${translations.displayOrder}</span> ${hutLabels}`;
    }

    // イベントリスナーを設定
    document.querySelectorAll('.tour-calendar-tabs').forEach(tabs => {
      tabs.addEventListener('click', handleTabClick);
    });

    document.querySelectorAll('.tour-calendar-mobile').forEach(mobile => {
      mobile.addEventListener('click', handleToggleClick);
    });

    console.log('Yamatrip: Tour calendar initialized');
  }

  // DOMContentLoaded で初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();